import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Phone, MapPin, AlertTriangle } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { DOCTOR, whatsappLink } from "@/config/doctorConfig";

const socialIcons = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin };

const quickLinks = [
  { label: "Home", href: "/#home" },
  { label: "About", href: "/#about" },
  { label: "Expertise", href: "/#expertise" },
  { label: "Publications", href: "/#publications" },
  { label: "Conditions", href: "/#conditions" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/#contact" },
  { label: "Book Appointment", href: "/#appointment" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Use", href: "/terms-of-use" },
  { label: "Medical Disclaimer", href: "/medical-disclaimer" },
];

export const Footer = () => (
  <footer className="bg-navy-950 text-slate-300 grain relative" data-testid="site-footer">
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-28 lg:pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="lg:col-span-2">
          <p className="font-serif font-bold text-white text-xl">{DOCTOR.name}</p>
          <p className="mt-1 text-xs font-mono tracking-wider text-teal-400 uppercase">{DOCTOR.qualifications}</p>
          <p className="mt-1 text-sm text-slate-400">{DOCTOR.role} · {DOCTOR.designation}</p>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
            Internal medicine physician in Bhopal providing evaluation and management of
            diabetes, hypertension, thyroid disorders, infections and adult medical conditions
            at {DOCTOR.hospitalName}, Saket Nagar.
          </p>
          <div className="mt-6 flex gap-3">
            {Object.entries(DOCTOR.socials).map(([key, url]) => {
              if (!url) return null;
              const Icon = socialIcons[key];
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${key} profile`}
                  data-testid={`footer-social-${key}`}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-teal-300 hover:border-teal-400/40 transition-colors duration-200"
                >
                  <Icon size={17} />
                </a>
              );
            })}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              data-testid="footer-social-whatsapp"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-teal-300 hover:border-teal-400/40 transition-colors duration-200"
            >
              <WhatsAppIcon size={16} />
            </a>
          </div>
        </div>

        <nav aria-label="Footer">
          <p className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-5">Quick Links</p>
          <ul className="space-y-2.5">
            {quickLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 hover:text-white transition-colors duration-200">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-5">Contact</p>
          <address className="not-italic space-y-4 text-sm">
            <p className="flex gap-3 leading-relaxed">
              <MapPin size={16} className="text-teal-400 shrink-0 mt-0.5" />
              <span>
                {DOCTOR.hospitalName}<br />
                {DOCTOR.addressLine1}, {DOCTOR.addressLine2}<br />
                {DOCTOR.addressLine3}
              </span>
            </p>
            <p className="flex gap-3 items-center">
              <Phone size={16} className="text-teal-400 shrink-0" />
              <span>
                Clinic: <a href={DOCTOR.clinicPhoneHref} data-testid="footer-clinic-phone" className="text-slate-200 hover:text-teal-300 transition-colors">{DOCTOR.clinicPhoneDisplay}</a>
                <br />
                Mobile: <a href={DOCTOR.mobileHref} data-testid="footer-mobile-phone" className="text-slate-200 hover:text-teal-300 transition-colors">{DOCTOR.mobileDisplay}</a>
              </span>
            </p>
            <a
              href={DOCTOR.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-profile-link"
              className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-white transition-colors duration-200"
            >
              <MapPin size={15} /> View on Google
            </a>
          </address>
        </div>
      </div>

      <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-3" data-testid="footer-disclaimer">
        <p className="text-xs text-slate-400 leading-relaxed">
          The information provided on this website is for general educational purposes only and
          should not be considered a substitute for an in-person medical consultation, diagnosis
          or treatment.
        </p>
        <p className="flex items-start gap-2 text-xs text-amber-300/90 leading-relaxed">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          In case of a medical emergency, please contact emergency services or visit the nearest
          emergency department immediately.
        </p>
      </div>

      <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-500">
          © {new Date().getFullYear()} {DOCTOR.name}. All rights reserved.
        </p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2">
          {legalLinks.map((l) => (
            <li key={l.label}>
              <Link to={l.href} data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs text-slate-500 hover:text-teal-300 transition-colors duration-200">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </footer>
);
